// ============================================
// COMPARISON BAR VIEW - Grouped Bar Chart
// ============================================

import { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, LabelList
} from 'recharts';
import type { Employee, CategoryStats } from '../../types/results';
import { getHebrewName } from '../../constants/muscles';
import { DEMOGRAPHIC_COLORS, DEMOGRAPHIC_LABELS } from '../../constants/demographics';
import CustomizedAxisTick from './CustomizedAxisTick';

interface ComparisonBarViewProps {
    employee: Employee;
    groupAvg: CategoryStats;
    globalAvg?: CategoryStats;  // Dynamic global averages from SQL
    hideTeam?: boolean;
    ageGroupAvg?: CategoryStats | null;
    genderGroupAvg?: CategoryStats | null;
}

export default function ComparisonBarView({
    employee,
    groupAvg,
    globalAvg,
    hideTeam,
    ageGroupAvg,
    genderGroupAvg
}: ComparisonBarViewProps) {
    const categories = Object.keys(employee.scores);

    const data = categories.map(category => ({
        name: getHebrewName(category),
        user: employee.scores[category] ?? 0,
        group: parseFloat((groupAvg[category] ?? 0).toFixed(2)),
        global: globalAvg?.[category] ?? 6.0,
        ...(ageGroupAvg ? { ageGroup: parseFloat((ageGroupAvg[category] ?? 0).toFixed(2)) } : {}),
        ...(genderGroupAvg ? { genderGroup: parseFloat((genderGroupAvg[category] ?? 0).toFixed(2)) } : {}),
    }));

    const hasDemographics = !!ageGroupAvg || !!genderGroupAvg;

    // Build legend items in the same order as the Bar components (left-to-right in SVG)
    // so that reading RTL, legend and bars match: user → group → gender → age → global
    const legendItems = useMemo(() => {
        const items: { name: string; color: string }[] = [];
        items.push({ name: 'ממוצע גלובלי', color: '#c17d5f' });
        if (ageGroupAvg) items.push({ name: DEMOGRAPHIC_LABELS.ageGroup, color: DEMOGRAPHIC_COLORS.ageGroup });
        if (genderGroupAvg) items.push({ name: DEMOGRAPHIC_LABELS.genderGroup, color: DEMOGRAPHIC_COLORS.genderGroup });
        if (!hideTeam) items.push({ name: 'ממוצע קבוצה', color: '#5e7d94' });
        items.push({ name: employee.name, color: '#133437' });
        return items;
    }, [employee.name, hideTeam, ageGroupAvg, genderGroupAvg]);

    return (
        <div className="analytics-bar-container" style={{
            direction: 'ltr',
            width: '100%',
            height: '100%',
            minHeight: '350px',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="name"
                        interval={0}
                        tick={<CustomizedAxisTick />}
                        height={80}
                        padding={{ left: 10, right: 10 }}
                    />
                    <YAxis
                        domain={[0, 10]}
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 11 }}
                    />
                    <Tooltip
                        cursor={{ fill: '#f4f1eb' }}
                        contentStyle={{
                            backgroundColor: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid #ebe6de',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            direction: 'rtl',
                            textAlign: 'right',
                            padding: '12px'
                        }}
                        itemStyle={{
                            padding: '2px 0'
                        }}
                        itemSorter={(item: any) => {
                            const order: Record<string, number> = { user: 0, group: 1, genderGroup: 2, ageGroup: 3, global: 4 };
                            return order[item.dataKey as string] ?? 5;
                        }}
                        formatter={(value: any, name?: string) => [
                            typeof value === 'number' ? value.toFixed(2) : value,
                            name || ''
                        ]}
                    />
                    <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        content={() => (
                            <ul style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                gap: '16px',
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                                direction: 'ltr',
                            }}>
                                {legendItems.map((item, index) => (
                                    <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <svg width="14" height="14">
                                            <rect width="14" height="14" fill={item.color} rx="2" />
                                        </svg>
                                        <span style={{ fontSize: '12px', color: '#5c5549' }}>{item.name}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    />

                    <Bar name="ממוצע גלובלי" dataKey="global" fill="#c17d5f" radius={[4, 4, 0, 0]}>
                        <LabelList
                            dataKey="global"
                            position="top"
                            style={{ fill: '#a8674d', fontSize: hasDemographics ? 8 : 10 }}
                            formatter={(v: any) => typeof v === 'number' ? v.toFixed(2) : v}
                        />
                    </Bar>
                    {ageGroupAvg && (
                        <Bar name={DEMOGRAPHIC_LABELS.ageGroup} dataKey="ageGroup" fill={DEMOGRAPHIC_COLORS.ageGroup} radius={[4, 4, 0, 0]}>
                            <LabelList
                                dataKey="ageGroup"
                                position="top"
                                style={{ fill: DEMOGRAPHIC_COLORS.ageGroup, fontSize: 8 }}
                                formatter={(v: any) => typeof v === 'number' ? v.toFixed(2) : v}
                            />
                        </Bar>
                    )}
                    {genderGroupAvg && (
                        <Bar name={DEMOGRAPHIC_LABELS.genderGroup} dataKey="genderGroup" fill={DEMOGRAPHIC_COLORS.genderGroup} radius={[4, 4, 0, 0]}>
                            <LabelList
                                dataKey="genderGroup"
                                position="top"
                                style={{ fill: DEMOGRAPHIC_COLORS.genderGroup, fontSize: 8 }}
                                formatter={(v: any) => typeof v === 'number' ? v.toFixed(2) : v}
                            />
                        </Bar>
                    )}
                    {!hideTeam && (
                        <Bar name="ממוצע קבוצה" dataKey="group" fill="#5e7d94" radius={[4, 4, 0, 0]}>
                            <LabelList
                                dataKey="group"
                                position="top"
                                style={{ fill: '#4d6a7e', fontSize: hasDemographics ? 8 : 10 }}
                                formatter={(v: any) => typeof v === 'number' ? v.toFixed(2) : v}
                            />
                        </Bar>
                    )}
                    <Bar name={employee.name} dataKey="user" fill="#133437" radius={[4, 4, 0, 0]}>
                        <LabelList
                            dataKey="user"
                            position="top"
                            style={{ fill: '#133437', fontSize: hasDemographics ? 8 : 10, fontWeight: 'bold' }}
                            formatter={(v: any) => typeof v === 'number' ? v.toFixed(2) : v}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
